import { useEffect } from "react";

/**
 * Asserts the language of the surface currently on screen by setting
 * {@code <html lang>} for as long as the calling component is mounted, then
 * restoring whatever was there before.
 *
 * <p>Why this exists: {@code src/i18n.ts} keeps {@code <html lang>} in sync with
 * the detected/selected i18n language, which is right for the public surface —
 * the landing page, sign-in and the legal pages all render real translations.
 * The authenticated app interior does not: its copy is still hardcoded English
 * (see the scope note in {@code i18n.ts}). A Danish visitor therefore ended up
 * with {@code <html lang="da">} wrapped around English text, which is an open
 * invitation for Chrome to offer an auto-translation — and Google Translate
 * rewrites text nodes React owns, crashing the reconciler with
 * "NotFoundError: Failed to execute 'removeChild' on 'Node'".</p>
 *
 * <p>Declaring the truth ({@code "en"} in the interior) heads off the offer at
 * the source, rather than only suppressing its effect. Drop the call from
 * {@link AppShell} once the interior is genuinely localized.</p>
 */
export function useHtmlLang(lang: string) {
  useEffect(() => {
    const el = document.documentElement;
    const previous = el.lang;
    el.lang = lang;
    return () => {
      el.lang = previous;
    };
  }, [lang]);
}
